import { backendServices } from '../../../../services';
import { FieldDictionaryViewModelWithMetadata, FormState } from './types';

const FIELD_DICTIONARY_SEARCHABLE_KEY_PROPERTIES = new Array<keyof FieldDictionaryViewModelWithMetadata>(
    'FieldDictionaryId',
    'Label',
    'FieldName'
);

function getFieldDictionarySearchableKey(field: FieldDictionaryViewModelWithMetadata): string {
    return FIELD_DICTIONARY_SEARCHABLE_KEY_PROPERTIES.map((property) => field[property]).join('|');
}

/**
 * Groups field dictionaries by section.
 *
 * @param fieldDictionaries - A list of unique field dictionaries to group by section.
 * @returns A record of section names to an array of field dictionary IDs.
 */
function groupFieldDictionariesBySection(
    fieldDictionaries: FieldDictionaryViewModelWithMetadata[]
): FormState['fieldDictionariesBySection'] {
    const fieldDictionariesBySection: FormState['fieldDictionariesBySection'] = {};

    fieldDictionaries.forEach((field) => {
        const sections = Array.from(field.metadata.sections);

        const searchableKey = getFieldDictionarySearchableKey(field);

        sections.forEach((section) => {
            fieldDictionariesBySection[section] ??= [];

            fieldDictionariesBySection[section].push(searchableKey);
        });
    });

    return fieldDictionariesBySection;
}

function getUniqueFieldDictionaries(
    documentLayout: backendServices.ViewModels.DocumentLayoutViewModel
): FormState['uniqueFieldDictionaries'] {
    const allFieldDictionaries = documentLayout.Layouts.flatMap((layout) => {
        const screenSizeUS = layout.ScreenSize_US;

        return layout.Cells.flatMap((cell) => {
            const sectionUS = cell.Section_US;

            return cell.Fields.map((field) => {
                return {
                    ...field,
                    screenSize: screenSizeUS,
                    section: sectionUS,
                };
            });
        });
    });

    const uniqueFieldDictionaries = new Map<number, FieldDictionaryViewModelWithMetadata>();

    allFieldDictionaries.forEach((field) => {
        const registeredField = uniqueFieldDictionaries.get(field.FieldDictionaryId);

        if (registeredField) {
            registeredField.metadata.screenSizes.add(field.screenSize);
            registeredField.metadata.sections.add(field.section);
            return;
        }

        const { screenSize, section, ...fieldWithoutMetadataProps } = field;

        uniqueFieldDictionaries.set(field.FieldDictionaryId, {
            ...fieldWithoutMetadataProps,
            metadata: {
                screenSizes: new Set([screenSize]),
                sections: new Set([section]),
                isDirty: false,
                isInvalid: false,
            },
        });
    });

    return uniqueFieldDictionaries;
}

export function documentLayoutToFormState(
    documentLayout: backendServices.ViewModels.DocumentLayoutViewModel
): FormState {
    const uniqueFieldDictionaries = getUniqueFieldDictionaries(documentLayout);

    const uniqueFieldDictionariesEntries = Array.from(uniqueFieldDictionaries.entries());

    const originalFieldDictionaries: typeof uniqueFieldDictionaries = new Map(
        JSON.parse(JSON.stringify(uniqueFieldDictionariesEntries))
    );

    return {
        uniqueFieldDictionaries,
        originalFieldDictionaries,
        fieldDictionariesBySection: groupFieldDictionariesBySection(Array.from(uniqueFieldDictionaries.values())),
        selectedFieldDictionaryId: null,
        searchQuery: '',
    };
}
